import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';

import { validateStudyProtocolID } from '../../../../services/StudyService';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { ToolTip } from '../../../common/tool-tip/ToolTip';
import { Checkbox } from '../../../common/checkbox/Checkbox';
import { CancelConfirmationModal } from '../../../common/cancel-confirmation-modal/CancelConfirmationModal';

import './StudyTrialCreateGeneralTab.css';

export const StudyTrialCreateGeneralTab = ({
  study,
  setCurrentScreen,
  navigateTo,
  context,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
  editMode,
  setShowSchedulesTab,
  handleSaveClick,
  setContext,
}) => {
  const [protocolPart1, setProtocolPart1] = useState('');
  const [protocolPart2, setProtocolPart2] = useState('');
  const [protocolPart3, setProtocolPart3] = useState('');
  const [protocolError, setProtocolError] = useState('');
  const [studyName, setStudyName] = useState('');
  const [studyNameError, setStudyNameError] = useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [typeError, setTypeError] = useState(null);
  const [description, setDescription] = useState('');
  const [coEnrolment, setCoEnrolment] = useState(false);
  const [studyDurationPerParticipant, setStudyDurationPerParticipant] = useState('');
  const [studyDurationPerParticipantError, setStudyDurationPerParticipantError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [dcAlertThreshold, setDcAlertThreshold] = useState('3');
  const [sdvAlertThreshold, setSdvAlertThreshold] = useState('3');
  const [dcAlertThresholdError, setDcAlertThresholdError] = useState(null);
  const [sdvAlertThresholdError, setSdvAlertThresholdError] = useState(null);

  // Only allow digits - 12345
  const numberPattern = /^\d*$/;

  const periodList = useRef(['Days', 'Months', 'Years']);
  const studyTypeList = useRef([
    'Clinical Trial',
    'Observational Study',
    'Programmes',
    'Prevention Study',
    'Research Study',
  ]);

  useEffect(() => {
    if (study && Object.keys(study).length !== 0) {
      buildProtocolId(study.protocolID);
      setStudyName(study.studyName || '');
      setSelectedType(study.type || null);
      setDescription(study.description || '');
      setCoEnrolment(study.coEnrolmentAllowed || false);
      setStudyDurationPerParticipant(study.studyDurationPerParticipant || '');
      setSelectedPeriod(study.studyDurationPerParticipantMetric || null);
      setDcAlertThreshold(study.alertThresholdForDataCapture || 3);
      setSdvAlertThreshold(study.alertThreshHoldForSDVCapture || 3);
    } else if (context?.breadcrumbs?.newStudyGeneralBreadcrumb?.data) {
      buildProtocolId(context.breadcrumbs.newStudyGeneralBreadcrumb.data.protocolID);
      setStudyName(context.breadcrumbs.newStudyGeneralBreadcrumb.data.studyName || '');
      setSelectedType(context.breadcrumbs.newStudyGeneralBreadcrumb.data.type || null);
      setDescription(context.breadcrumbs.newStudyGeneralBreadcrumb.data.description || '');
      setCoEnrolment(context.breadcrumbs.newStudyGeneralBreadcrumb.data.coEnrolmentAllowed || false);
      setStudyDurationPerParticipant(
        context.breadcrumbs.newStudyGeneralBreadcrumb.data.studyDurationPerParticipant || ''
      );
      setSelectedPeriod(context.breadcrumbs.newStudyGeneralBreadcrumb.data.studyDurationPerParticipantMetric || null);
      setDcAlertThreshold(context.breadcrumbs.newStudyGeneralBreadcrumb.data.alertThresholdForDataCapture || 3);
      setSdvAlertThreshold(context.breadcrumbs.newStudyGeneralBreadcrumb.data.alertThreshHoldForSDVCapture || 3);
    }
  }, [study, context.breadcrumbs]);

  const buildProtocolId = (protocolId) => {
    const protocolIdParts = protocolId.split('-');
    const protocolPart1 = protocolIdParts[0].replace('AUR', '');
    const protocolPart2 = protocolIdParts[1];
    const protocolPart3 = protocolIdParts[2];

    setProtocolPart1(protocolPart1);
    setProtocolPart2(protocolPart2);
    setProtocolPart3(protocolPart3);
  };

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  const handlePart1Change = (e) => {
    const value = e.target.value;
    if (/^\d{0,1}$/.test(value)) {
      setProtocolPart1(value);
    }

    if (protocolPart1 && protocolPart2 && protocolPart3) {
      validateProtocolID();
    }
  };

  const handlePart2Change = (e) => {
    const value = e.target.value;
    if (/^\d{0,1}$/.test(value)) {
      setProtocolPart2(value);
    }
  };

  const handlePart3Change = (e) => {
    const value = e.target.value;
    if (/^\d{0,3}$/.test(value)) {
      setProtocolPart3(value);
    }
  };

  const validateProtocolID = async () => {
    const protocolIdToValidate = 'AUR' + protocolPart1 + '-' + protocolPart2 + '-' + protocolPart3;
    setSpinnerText('Validating Protocol ID');
    const [data, error] = await validateStudyProtocolID(context.username, protocolIdToValidate);
    if (data) {
      if (data.responseStatus === 'Success') {
        if (data.protocolIDAlreadyExists) {
          setProtocolError('Protocol ID already exists.');
        } else {
          setProtocolError('');
        }
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(null);
  };

  const handleYesChange = () => {
    setCoEnrolment(true);
  };

  const handleNoChange = () => {
    setCoEnrolment(false);
  };

  const handleStudyTypeChange = (selectedStudyTypeOption) => {
    setSelectedType(selectedStudyTypeOption.value);
  };

  const handlePeriodChange = (selectedOption) => {
    setSelectedPeriod(selectedOption.value);
  };

  const validateRequiredFields = () => {
    if (!protocolPart1 || !protocolPart2 || !protocolPart3) {
      setProtocolError('*REQUIRED');
    } else {
      validateProtocolID();
    }

    if (!studyName) {
      setStudyNameError('*REQUIRED');
    } else {
      setStudyNameError('');
    }

    if (!selectedType) {
      setTypeError('*REQUIRED');
    } else {
      setTypeError('');
    }

    if (studyDurationPerParticipant < 0) {
      setStudyDurationPerParticipantError('*Value Cannot be less than 0.');
    } else {
      setStudyDurationPerParticipantError('');
    }

    if (dcAlertThreshold < 0) {
      setDcAlertThresholdError('*Value Cannot be less than 0.');
    } else {
      setDcAlertThresholdError('');
    }

    if (sdvAlertThreshold < 0) {
      setSdvAlertThresholdError('*Value Cannot be less than 0.');
    } else {
      setSdvAlertThresholdError('');
    }
  };

  const handleSaveAndNextClick = () => {
    validateRequiredFields();

    if (!protocolPart1 || !protocolPart2 || !protocolPart3 || !studyName || !selectedType) {
      return;
    }

    let protocolIdToSave = 'AUR' + protocolPart1 + '-' + protocolPart2 + '-' + protocolPart3;

    const newStudy = {
      protocolID: protocolIdToSave,
      studyName: studyName,
      type: selectedType,
      description: description,
      coEnrolmentAllowed: coEnrolment,
      studyDurationPerParticipant: studyDurationPerParticipant,
      studyDurationPerParticipantMetric: selectedPeriod,
      alertThresholdForDataCapture: dcAlertThreshold,
      alertThreshHoldForSDVCapture: sdvAlertThreshold,
    };
    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        newStudyGeneralBreadcrumb: {
          ...prevState.breadcrumbs.newStudyGeneralBreadcrumb,
          type: 'General',
          screenKey: 'studyTrialCreateGeneral',
          data: newStudy,
        },
      },
      editMode: true,
    }));

    handleSaveClick('studySchedules');
    setShowSchedulesTab(true);
  };

  const handleCancelClick = () => {
    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        newStudyGeneralBreadcrumb: undefined,
        newStudyFormsBreadcrumb: undefined,
        newStudySchedulesBreadcrumb: undefined,
        newStudySitesBreadcrumb: undefined,
      },
      editMode: true,
    }));
    navigateTo('studyTrialAdmin');
  };

  return (
    <div className="study-trial-create-general-tab">
      <div className="study-trial-general-section">
        <div className="input-container">
          <label className={'input-label'}>Protocol ID:</label>
          <label className={'study-trial-protocol-label'}>AUR</label>
          <input
            className={`text-input-small ${protocolError && 'error-border'}`}
            type="text"
            value={protocolPart1}
            onChange={handlePart1Change}
          />
          <label className={'study-trial-protocol-label'}>-</label>
          <input
            className={`text-input-small ${protocolError && 'error-border'}`}
            type="text"
            value={protocolPart2}
            onChange={handlePart2Change}
          />
          <label className={'study-trial-protocol-label'}>-</label>
          <input
            className={`text-input-small ${protocolError && 'error-border'}`}
            type="text"
            value={protocolPart3}
            onChange={handlePart3Change}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'protocolID123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Protocol ID:'}
            />
          )}
        </div>
        {protocolError && (
          <div className="studies-error-message">
            <div className="small-red-text">{protocolError}</div>
          </div>
        )}
        <div className="input-container">
          <label className={'input-label'}>Study Name:</label>
          <input
            className={`text-input ${studyNameError && 'error-border'}`}
            type="text"
            value={studyName}
            onChange={({ target }) => {
              setStudyName(target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyName123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Study Name:'}
            />
          )}
        </div>
        <div className="studies-error-message">
          {studyNameError && <div className="small-red-text">{studyNameError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Type:</label>
          <Select
            options={studyTypeList.current.map((studyType, index) => ({
              value: studyType,
              label: studyType,
            }))}
            closeMenuOnSelect={true}
            onChange={handleStudyTypeChange}
            value={selectedType ? { value: selectedType, label: selectedType } : null}
            classNamePrefix={`input-select${typeError ? 'error-border' : ''}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyType123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Types:'}
            />
          )}
        </div>
        <div className="studies-error-message">{typeError && <div className="small-red-text">{typeError}</div>}</div>
        <div className="input-container">
          <label className={'input-area-label'}>Description:</label>
          <textarea
            className="input-area"
            spellCheck={true}
            type="text"
            value={description}
            onChange={({ target }) => {
              setDescription(target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyDescription123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Description:'}
            />
          )}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Co-enrolment Allowed:</label>
          <Checkbox onChange={handleYesChange} label={'Yes'} checked={coEnrolment} fillStyle={true} color={'#000000'} />
          <Checkbox onChange={handleNoChange} label={'No'} checked={!coEnrolment} fillStyle={true} color={'#000000'} />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studycoEnrolment123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Co-enrolment allowed:'}
            />
          )}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Study Duration Per Participant:</label>
          <input
            className={`text-input-small ${studyDurationPerParticipantError && 'error-border'}`}
            style={{ marginRight: 10 }}
            type="text"
            value={studyDurationPerParticipant}
            onChange={({ target }) => {
              const value = target.value;
              if (numberPattern.test(value)) {
                setStudyDurationPerParticipant(target.value);
              }
            }}
            pattern="\d*"
          />
          <Select
            options={periodList.current.map((period, index) => ({
              value: period,
              label: period,
            }))}
            closeMenuOnSelect={true}
            onChange={handlePeriodChange}
            value={selectedPeriod ? { value: selectedPeriod, label: selectedPeriod } : null}
            classNamePrefix="input-select"
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyDurationPerParticipant123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Study Duration Per Participant:'}
            />
          )}
        </div>
        <div className="studies-error-message">
          {studyDurationPerParticipantError && <div className="small-red-text">{studyDurationPerParticipantError}</div>}
        </div>

        <div className="input-container">
          <label className={'input-label'}>Alert Threshold for Data Capture:</label>
          <input
            className={`text-input-small ${dcAlertThresholdError && 'error-border'}`}
            type="text"
            value={dcAlertThreshold}
            onChange={({ target }) => {
              const value = target.value;
              if (numberPattern.test(value)) {
                setDcAlertThreshold(target.value);
              }
            }}
            pattern="\d*"
          />
          <label className={'input-label-back'}> days after Visit Date.</label>
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'dataCaptureAlertThreshold123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Alert Threshold for Data Capture:'}
            />
          )}
        </div>
        <div className="studies-error-message">
          {dcAlertThresholdError && <div className="small-red-text">{dcAlertThresholdError}</div>}
        </div>

        <div className="input-container">
          <label className={'input-label'}>Alert Threshold for SDV Capture:</label>
          <input
            className={`text-input-small ${sdvAlertThresholdError && 'error-border'}`}
            type="text"
            value={sdvAlertThreshold}
            onChange={({ target }) => {
              const value = target.value;
              if (numberPattern.test(value)) {
                setSdvAlertThreshold(value);
              }
            }}
            pattern="\d*"
          />
          <label className={'input-label-back'}> days after Data Capture Date.</label>
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'sdvCaptureAlertThreshold123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Alert Threshold for SDV Capture:'}
            />
          )}
        </div>
        <div className="studies-error-message">
          {sdvAlertThresholdError && <div className="small-red-text">{sdvAlertThresholdError}</div>}
        </div>
      </div>
      {isCancelModalOpen && (
        <CancelConfirmationModal
          closeCancelModal={closeCancelModal}
          handleCancelClick={handleCancelClick}
          setIsCancelModalOpen={setIsCancelModalOpen}
        />
      )}
      <div className="study-button-container">
        {editMode && (
          <>
            <SaveButton
              color={'#077187'}
              title={'Save Changes'}
              onClick={() => {
                setCurrentScreen('studySchedules');
              }}
            />
          </>
        )}
        {!editMode && (
          <>
            <SaveButton
              color={'#ffffff'}
              border={'1px solid #077187'}
              textColor={'#077187'}
              title={'Cancel'}
              onClick={() => setIsCancelModalOpen(true)}
            />
            <SaveButton color={'#077187'} title={'Save and Next'} onClick={handleSaveAndNextClick} />
          </>
        )}
      </div>
    </div>
  );
};
